import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import IssueReporter from './issues/IssueReporter';
import { Outlet, Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';
import { useAuth, usePermissions, ViewAsUserInput } from '../AuthContext';
import {
  AppBar,
  Box,
  CssBaseline,
  Dialog,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Toolbar,
  Typography,
  Divider,
  Paper,
  useTheme,
  useMediaQuery,
  ListItemIcon,
  FormControl,
  Select,
  Menu,
  MenuItem,
  Tooltip,
  Popover,
  Badge,
  Button,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import WorkOutlineOutlinedIcon from '@mui/icons-material/WorkOutlineOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import TravelExploreOutlinedIcon from '@mui/icons-material/TravelExploreOutlined';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import CloseIcon from '@mui/icons-material/Close';
import TodayOutlinedIcon from '@mui/icons-material/TodayOutlined';
import MeetingRoomOutlinedIcon from '@mui/icons-material/MeetingRoomOutlined';
import { collection, doc, getFirestore, limit, onSnapshot, orderBy, query, serverTimestamp, setDoc, where } from 'firebase/firestore';
import { CAREER_SOURCE, normalizeApplicationStatus } from '../utils/careerApplications';
import { useMyTodayUrgent, visitPhase } from './myTodayUrgent';
import { useReceptionToday } from '../utils/reception';
import WhatsAppNotificationPanel from './WhatsAppNotificationPanel';

// ─── Design Tokens ────────────────────────────────────────────────────────────
const GOLD             = '#9d6f32';
const DARK             = '#1a1a1a';
const MUTED            = '#6b6b6b';
const BORDER           = '#e8e8e8';
const SIDEBAR_BG       = '#fafaf9';
const SIDEBAR_TEXT     = '#6b6b6b';
const SIDEBAR_DIVIDER  = '#e8e8e8';
const SIDEBAR_HOVER    = 'rgba(0,0,0,0.04)';
const SIDEBAR_SEL_BG   = 'rgba(157,111,50,0.08)';
const FONT_HEADING     = "'Georgia', 'Times New Roman', serif";
const FONT_BODY        = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
const APPBAR_H         = 64; // px — MUI Toolbar default on desktop
// ─────────────────────────────────────────────────────────────────────────────

const drawerWidth  = 240;
const collapsedWidth = 64;
const APP_MODE_STORAGE_KEY = 'swarna_app_mode';
const OFFICE_PROJECT_PICKER_ON_ENTER_KEY = 'office:showProjectPickerOnEnter';
const APP_MODE_PICKER_SKIP_ONCE_KEY = 'swarna_app_mode_picker_skip_once';
const APP_MODE_SWITCH_BYPASS_KEY = 'swarna_app_mode_switch_bypass_once';
type AppMode = 'crm' | 'office';
type ReminderStatus = 'upcoming' | 'due' | 'overdue';
type ReminderItem = {
  id: string;
  name: string;
  kind: 'followup' | 'visit';
  reminderKey: string;
  status: ReminderStatus;
  dueAt: Date;
  minutesFromNow: number;
};
const navItems = [
  { text: 'Today',           path: '/today',            pageKey: 'today',           icon: <TodayOutlinedIcon /> },
  { text: 'Front desk',      path: '/front-desk',       pageKey: 'frontDesk',       icon: <MeetingRoomOutlinedIcon /> },
  { text: 'Dashboard',       path: '/dashboard',        pageKey: 'dashboard',       icon: <DashboardOutlinedIcon /> },
  { text: 'All Leads',       path: '/leads',            pageKey: 'leads',           icon: <TableChartOutlinedIcon /> },
  { text: 'Follow-ups',      path: '/follow-ups',       pageKey: 'followUps',       icon: <AssignmentTurnedInOutlinedIcon /> },
  { text: 'Brokers',         path: '/brokers',          pageKey: 'brokers',         icon: <HandshakeOutlinedIcon /> },
  { text: 'Job Applications',path: '/job-applications', pageKey: 'jobApplications', icon: <WorkOutlineOutlinedIcon /> },
  { text: 'Messages',        path: '/messages',         pageKey: 'messages',        icon: <ChatBubbleOutlineIcon /> },
  { text: 'WhatsApp',        path: '/whatsapp',         pageKey: 'whatsappHub',     icon: <WhatsAppIcon /> },
  { text: 'Meta Leads',      path: '/meta-leads',       pageKey: 'metaLeads',       icon: <InboxOutlinedIcon /> },
  { text: 'SEO & Local SXO', path: '/seo',              pageKey: 'seo',             icon: <TravelExploreOutlinedIcon /> },
  { text: 'Website CMS',     path: '/website-cms',      pageKey: 'websiteCms',      icon: <LanguageOutlinedIcon /> },
  { text: 'Campaigns',       path: '/campaigns',        pageKey: 'campaigns',       icon: <CampaignOutlinedIcon /> },
  { text: 'Reports',         path: '/reports',          pageKey: 'reports',         icon: <AssessmentOutlinedIcon /> },
];

function toDateMaybe(v: any): Date | null {
  if (!v) return null;
  if (typeof v?.toDate === 'function') {
    const d = v.toDate();
    return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null;
  }
  if (typeof v === 'string' || typeof v === 'number') {
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

const META_LEADS_CUTOVER = new Date('2026-09-20T00:00:00+05:30');

function isMetaLeadAfterCutover(metaCreatedAt: unknown, receivedAt: unknown) {
  const submittedAt = toDateMaybe(metaCreatedAt) || toDateMaybe(receivedAt);
  return Boolean(submittedAt && submittedAt >= META_LEADS_CUTOVER);
}

function isClosedLike(status: unknown): boolean {
  const s = String(status || '').trim().toLowerCase();
  return s === 'closed' || s === 'completed' || s === 'cancelled';
}

function effectiveFollowUpAt(lead: any): Date | null {
  const explicit = toDateMaybe(lead?.nextFollowUpAt);
  if (explicit) return explicit;
  const byDate = toDateMaybe(lead?.nextFollowUpDate);
  if (!byDate) return null;
  const fallback = new Date(byDate);
  fallback.setHours(10, 0, 0, 0);
  return fallback;
}

export default function DashboardLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopOpen, setDesktopOpen] = useState(false);
  const { canPage } = usePermissions();
  const {
    currentUser,
    actorUser,
    roleId,
    projectIds,
    currentProjectId,
    setCurrentProjectId,
    canUseViewAs,
    viewingAs,
    startViewAs,
    stopViewAs,
  } = useAuth();
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();
  const navigate = useNavigate();
  const db = useMemo(() => getFirestore(), []);
  const [projects, setProjects] = useState<any[]>([]);
  const [modePickerOpen, setModePickerOpen] = useState(false);
  const [modePickerChecked, setModePickerChecked] = useState(false);
  const [reminders, setReminders] = useState<ReminderItem[]>([]);
  const [reminderAnchorEl, setReminderAnchorEl] = useState<HTMLElement | null>(null);
  const [readReminderKeys, setReadReminderKeys] = useState<Set<string>>(new Set());
  const [newApplicationCount, setNewApplicationCount] = useState(0);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const [pendingMetaLeadCount, setPendingMetaLeadCount] = useState(0);
  const [pendingBrokerProfileCount, setPendingBrokerProfileCount] = useState(0);
  const [pendingBrokerApprovalCount, setPendingBrokerApprovalCount] = useState(0);
  const [viewAsUsers, setViewAsUsers] = useState<ViewAsUserInput[]>([]);
  const [viewAsAnchorEl, setViewAsAnchorEl] = useState<HTMLElement | null>(null);
  const isAdmin = String(roleId || '') === 'super-admin' || String(roleId || '') === 'admin';
  const appMode: AppMode = location.pathname.startsWith('/office') ? 'office' : 'crm';
  const isFrontDeskPage = location.pathname === '/front-desk';
  const remindersOpen = Boolean(reminderAnchorEl);
  const headerHeight = APPBAR_H + (viewingAs ? 42 : 0);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleDesktopToggle = () => setDesktopOpen((prev) => !prev);
  const handleLogout = () => signOut(auth);

  useEffect(() => {
    if (!canUseViewAs) {
      setViewAsUsers([]);
      return;
    }
    return onSnapshot(collection(db, 'users'), (snapshot) => {
      const rows = snapshot.docs.map((entry) => {
        const data = entry.data() as Record<string, any>;
        return {
          uid: entry.id,
          email: String(data.email || '').trim(),
          name: String(data.name || data.displayName || data.email || 'Team member').trim(),
          roleId: String(data.roleId || data.role || 'user').trim().toLowerCase(),
          projectIds: Array.isArray(data.projectIds) ? data.projectIds.filter((value): value is string => typeof value === 'string') : [],
          officeProjectIds: Array.isArray(data.officeProjectIds) ? data.officeProjectIds.filter((value): value is string => typeof value === 'string') : [],
          pageAccess: Array.isArray(data.pageAccess) ? data.pageAccess.filter((value): value is string => typeof value === 'string') : [],
          actionAccess: Array.isArray(data.actionAccess) ? data.actionAccess.filter((value): value is string => typeof value === 'string') : [],
          officePageAccess: Array.isArray(data.officePageAccess) ? data.officePageAccess.filter((value): value is string => typeof value === 'string') : [],
          officeActionAccess: Array.isArray(data.officeActionAccess) ? data.officeActionAccess.filter((value): value is string => typeof value === 'string') : [],
          disabled: data.disabled === true || String(data.status || '').toLowerCase() === 'inactive',
        };
      }).filter((user) =>
        !user.disabled &&
        user.uid !== actorUser?.uid &&
        user.email.toLowerCase() !== String(actorUser?.email || '').toLowerCase() &&
        !['admin', 'super-admin'].includes(user.roleId)
      ).map(({ disabled: _disabled, ...user }) => user);
      setViewAsUsers(rows.sort((a, b) => a.name.localeCompare(b.name)));
    }, () => setViewAsUsers([]));
  }, [actorUser?.email, actorUser?.uid, canUseViewAs, db]);

  const visibleViewAsUsers = viewAsUsers.filter((user) =>
    !currentProjectId || user.projectIds.length === 0 || user.projectIds.includes(currentProjectId)
  );

  const selectViewAsUser = async (uid: string) => {
    setViewAsAnchorEl(null);
    if (!uid) {
      stopViewAs();
      navigate('/', { replace: true });
      return;
    }
    const selected = viewAsUsers.find((user) => user.uid === uid);
    if (!selected) return;
    await startViewAs(selected);
    navigate('/', { replace: true });
  };

  const blockPreviewInteraction = (event: React.SyntheticEvent) => {
    if (!viewingAs) return;
    const target = event.target as HTMLElement | null;
    if (!target || target.closest('a,[data-view-as-allow="true"]')) return;
    if (target.closest('button,input,select,textarea,[role="button"],[role="checkbox"],[contenteditable="true"]')) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const isManagerOrAdmin = isAdmin || String(roleId || '') === 'sales-manager';
  const visibleNavItems = navItems.filter((item) =>
    item.pageKey === 'today' || item.pageKey === 'frontDesk' ? isManagerOrAdmin || canPage(item.pageKey) :
    item.pageKey === 'whatsappHub' ? isAdmin :
    (item.pageKey === 'metaLeads' || item.pageKey === 'seo' || item.pageKey === 'websiteCms') ? isAdmin || canPage(item.pageKey) : canPage(item.pageKey)
  );
  const hasCrmAccess = visibleNavItems.length > 0;
  const hasOfficeAccess = isAdmin || canPage('office');
  const navExpanded = isSmDown ? true : desktopOpen;
  const [badgeNow, setBadgeNow] = useState(() => new Date());
  const [todayTeam, setTodayTeam] = useState(() => {
    try { return window.localStorage.getItem('crm:today:team') !== '0'; } catch { return true; }
  });
  const showTodayBadge = Boolean(currentUser && appMode === 'crm' && visibleNavItems.some((item) => item.pageKey === 'today'));
  const showFrontDeskBadge = Boolean(currentUser && appMode === 'crm' && visibleNavItems.some((item) => item.pageKey === 'frontDesk'));
  const todayActions = useMyTodayUrgent(db, String(currentUser?.email || ''), true, todayTeam, showTodayBadge);
  const receptionToday = useReceptionToday(db, showFrontDeskBadge);
  const todayActionCount = showTodayBadge
    ? todayActions.filter((item) => item.bucket === 'today' && (item.kind !== 'visit' || visitPhase(item, badgeNow) === 'unmarked')).length
    : 0;
  const frontDeskWaitingCount = showFrontDeskBadge ? receptionToday.filter((visit) => visit.status === 'waiting').length : 0;

  useEffect(() => {
    if (!showTodayBadge) return;
    const timer = window.setInterval(() => setBadgeNow(new Date()), 60000);
    return () => window.clearInterval(timer);
  }, [showTodayBadge]);

  useEffect(() => {
    const syncTodayScope = () => {
      try { setTodayTeam(window.localStorage.getItem('crm:today:team') !== '0'); } catch { setTodayTeam(true); }
    };
    window.addEventListener('crm:today:scope-change', syncTodayScope);
    window.addEventListener('storage', syncTodayScope);
    return () => {
      window.removeEventListener('crm:today:scope-change', syncTodayScope);
      window.removeEventListener('storage', syncTodayScope);
    };
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setDesktopOpen(false);
  }, [location.pathname]);

  useLayoutEffect(() => {
    if (!currentUser) {
      setModePickerChecked(false);
      setModePickerOpen(false);
      return;
    }
    if (modePickerChecked) return;
    if (!(hasCrmAccess && hasOfficeAccess)) {
      setModePickerOpen(false);
      setModePickerChecked(true);
      return;
    }
    const switched = window.localStorage.getItem(APP_MODE_SWITCH_BYPASS_KEY) === '1';
    if (switched) {
      window.localStorage.removeItem(APP_MODE_SWITCH_BYPASS_KEY);
      setModePickerOpen(false);
      setModePickerChecked(true);
      return;
    }
    const skipOnce = window.localStorage.getItem(APP_MODE_PICKER_SKIP_ONCE_KEY) === '1';
    if (skipOnce) {
      window.localStorage.removeItem(APP_MODE_PICKER_SKIP_ONCE_KEY);
      setModePickerOpen(false);
      setModePickerChecked(true);
      return;
    }
    setModePickerOpen(true);
    setModePickerChecked(true);
  }, [currentUser, hasCrmAccess, hasOfficeAccess, modePickerChecked]);

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'projects'), orderBy('createdAt', 'desc')),
      (snap) => {
        const next = snap.docs.map((d) => {
          const v = d.data() as any;
          return {
            id: d.id,
            name: typeof v?.name === 'string' ? v.name : null,
            unitTypes: Array.isArray(v?.unitTypes) ? v.unitTypes : null,
            createdAt: v?.createdAt ?? null,
          };
        });
        setProjects((prev) => {
          if (!Array.isArray(prev) || prev.length !== next.length) return next as any;
          for (let i = 0; i < next.length; i += 1) {
            const a: any = prev[i];
            const b: any = next[i];
            if (!a || !b) return next as any;
            if (String(a.id) !== String(b.id)) return next as any;
            if (String(a.name || '') !== String(b.name || '')) return next as any;
            const aU = Array.isArray(a.unitTypes) ? a.unitTypes : [];
            const bU = Array.isArray(b.unitTypes) ? b.unitTypes : [];
            if (aU.length !== bU.length) return next as any;
            for (let j = 0; j < aU.length; j += 1) {
              if (String(aU[j] || '') !== String(bU[j] || '')) return next as any;
            }
          }
          return prev;
        });
      },
      () => setProjects([])
    );
    return () => unsub();
  }, [db]);

  useEffect(() => {
    if (!canPage('jobApplications')) {
      setNewApplicationCount(0);
      return;
    }
    const applicationsQuery = query(
      collection(db, 'leads'),
      where('source', '==', CAREER_SOURCE)
    );
    const unsub = onSnapshot(
      applicationsQuery,
      (snap) => {
        const next = snap.docs.reduce((count, docSnap) => {
          const status = normalizeApplicationStatus(
            (docSnap.data() as { applicationStatus?: unknown }).applicationStatus
          );
          return count + (status === 'New' ? 1 : 0);
        }, 0);
        setNewApplicationCount(next);
      },
      () => setNewApplicationCount(0)
    );
    return () => unsub();
  }, [canPage, db]);

  useEffect(() => {
    if (!currentUser || !isAdmin || !canPage('brokers')) {
      setPendingBrokerProfileCount(0);
      return;
    }
    return onSnapshot(
      query(collection(db, 'brokerProfileSubmissions'), where('status', '==', 'pending')),
      (snap) => setPendingBrokerProfileCount(snap.size),
      () => setPendingBrokerProfileCount(0)
    );
  }, [canPage, currentUser, db, isAdmin]);

  useEffect(() => {
    if (!currentUser || !isAdmin) {
      setPendingBrokerApprovalCount(0);
      return;
    }
    return onSnapshot(
      query(collection(db, 'brokers'), where('approvalStatus', '==', 'pending')),
      (snap) => setPendingBrokerApprovalCount(snap.size),
      () => setPendingBrokerApprovalCount(0)
    );
  }, [currentUser, db, isAdmin]);

  useEffect(() => {
    const email = String(currentUser?.email || '').trim();
    if (!email) { setReminders([]); return; }
    const q = query(collection(db, 'leads'), where('assignedTo', '==', email));
    const unsub = onSnapshot(q, (snap) => {
      const now = new Date();
      const visitUpcomingWindowMs = 60 * 60 * 1000;
      const dueWindowMs = 15 * 60 * 1000;
      const overdueRetentionMs = 7 * 24 * 60 * 60 * 1000;
      const next = snap.docs
        .map((d) => ({ id: d.id, ...(d.data() as any) }))
        .filter((lead: any) => !isClosedLike(lead?.status))
        .flatMap((lead: any) => {
          const out: ReminderItem[] = [];
          const leadId = String(lead.id);
          const leadName = String(lead.name || 'Lead');
          const followDueAt = effectiveFollowUpAt(lead);
          if (followDueAt) {
            const diffMs = followDueAt.getTime() - now.getTime();
            let status: ReminderStatus | null = null;
            if (diffMs <= 0 && Math.abs(diffMs) <= dueWindowMs) status = 'due';
            else if (diffMs < -dueWindowMs && Math.abs(diffMs) <= overdueRetentionMs) status = 'overdue';
            if (status) {
              out.push({ id: leadId, name: leadName, kind: 'followup', reminderKey: `lead_${leadId}_followup_${followDueAt.getTime()}`, status, dueAt: followDueAt, minutesFromNow: Math.round(diffMs / 60000) });
            }
          }
          const appt = lead?.appointment || null;
          const isVisitScheduled = String(appt?.status || '').trim().toLowerCase() === 'scheduled' && String(appt?.type || '').trim().toLowerCase() === 'site-visit';
          const visitAt = toDateMaybe(appt?.scheduledAt);
          if (isVisitScheduled && visitAt) {
            const diffMs = visitAt.getTime() - now.getTime();
            let status: ReminderStatus | null = null;
            if (diffMs >= 0 && diffMs <= visitUpcomingWindowMs) status = 'upcoming';
            else if (diffMs < 0 && Math.abs(diffMs) <= dueWindowMs) status = 'due';
            else if (diffMs < -dueWindowMs && Math.abs(diffMs) <= overdueRetentionMs) status = 'overdue';
            if (status) {
              out.push({ id: leadId, name: leadName, kind: 'visit', reminderKey: `lead_${leadId}_visit_${visitAt.getTime()}`, status, dueAt: visitAt, minutesFromNow: Math.round(diffMs / 60000) });
            }
          }
          return out;
        })
        .sort((a, b) => a.dueAt.getTime() - b.dueAt.getTime());
      setReminders(next);
    }, () => setReminders([]));
    return () => unsub();
  }, [currentUser?.email, db]);

  useEffect(() => {
    const uid = String(currentUser?.uid || '').trim();
    if (!uid) { setReadReminderKeys(new Set()); return; }
    const readsRef = collection(db, 'users', uid, 'reminderReads');
    const unsub = onSnapshot(readsRef, (snap) => {
      const next = new Set<string>();
      snap.docs.forEach((d) => next.add(String(d.id)));
      setReadReminderKeys(next);
    }, () => setReadReminderKeys(new Set()));
    return () => unsub();
  }, [currentUser?.uid, db]);

  const markReminderRead = async (item: ReminderItem) => {
    const uid = String(currentUser?.uid || '').trim();
    if (!uid) return;
    try {
      await setDoc(
        doc(db, 'users', uid, 'reminderReads', item.reminderKey),
        { leadId: item.id, kind: item.kind, status: item.status, dueAtMs: item.dueAt.getTime(), readAt: serverTimestamp(), lastClickedAt: serverTimestamp() },
        { merge: true }
      );
      setReadReminderKeys((prev) => { const next = new Set(prev); next.add(item.reminderKey); return next; });
    } catch { /* Best-effort */ }
  };

  const visibleProjects = useMemo(() => {
    const r = (roleId || '').toString();
    const isAdminRole = r === 'super-admin' || r === 'admin';
    if (isAdminRole && (!projectIds || projectIds.length === 0)) return projects;
    const allowed = new Set((projectIds || []).filter(Boolean));
    return projects.filter((p) => allowed.has(p.id));
  }, [projectIds, projects, roleId]);

  const projectSelectValue = useMemo(() => {
    if (visibleProjects.length === 0) return '';
    if (currentProjectId && visibleProjects.some((p) => p.id === currentProjectId)) return currentProjectId;
    return '';
  }, [currentProjectId, visibleProjects]);

  useEffect(() => {
    if (!currentUser) return;
    if (visibleProjects.length === 0) { if (currentProjectId) setCurrentProjectId(null); return; }
    const ok = currentProjectId && visibleProjects.some((p) => p.id === currentProjectId);
    if (!ok) {
      const preferredProject = visibleProjects.find((p) => String((p as any)?.name || '').trim().toLowerCase() === 'swarna avsar');
      setCurrentProjectId((preferredProject || visibleProjects[0]).id);
    }
  }, [currentProjectId, currentUser, setCurrentProjectId, visibleProjects]);

  useEffect(() => {
    if (!currentUser) return;
    // Sales team: count their own chats with unread messages (chat cards).
    if (!['admin', 'super-admin', 'sales-manager'].includes(String(roleId || '').toLowerCase())) {
      return onSnapshot(
        query(collection(db, 'whatsappChats'), where('assignedTo', '==', String(currentUser.email || '').toLowerCase()), orderBy('lastMessageAt', 'desc'), limit(500)),
        (snap) => setUnreadMessageCount(snap.docs.filter((d) => Number((d.data() as any).unreadCount || 0) > 0).length),
        () => setUnreadMessageCount(0)
      );
    }
    const q = query(
      collection(db, 'whatsappMessages'),
      orderBy('createdAt', 'desc'),
      limit(500)
    );
    const unsub = onSnapshot(q, (snap) => {
      const contacts = new Set<string>();
      snap.docs.forEach((d) => {
        const data = d.data();
        if (String(data.direction || '') !== 'inbound' || data.readAt) return;
        const phone = String(data.from || data.contactPhone || '').trim();
        const channel = String(data.channel || 'whatsapp').trim() || 'whatsapp';
        if (phone) contacts.add(`${channel}:${phone}`);
      });
      setUnreadMessageCount(contacts.size);
    }, () => setUnreadMessageCount(0));
    return unsub;
  }, [currentUser, db, roleId]);

  useEffect(() => {
    if (!currentUser || !(isAdmin || canPage('metaLeads'))) {
      setPendingMetaLeadCount(0);
      return;
    }
    const unsub = onSnapshot(
      query(collection(db, 'metaLeadSubmissions'), orderBy('receivedAt', 'desc'), limit(300)),
      (snap) => setPendingMetaLeadCount(snap.docs.filter((item) => {
        const lead = item.data();
        return String(lead.status || 'pending') === 'pending'
          && isMetaLeadAfterCutover(lead.metaCreatedAt, lead.receivedAt);
      }).length),
      () => setPendingMetaLeadCount(0),
    );
    return unsub;
  }, [currentUser, db, isAdmin, canPage]);

  // ─── Sidebar content (shared by mobile + desktop drawers) ──────────────────
  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: SIDEBAR_BG }}>

      {/* Mobile-only header */}
      {isSmDown && (
        <>
          <Box sx={{
            px: 2, py: 1.5,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            borderBottom: `1px solid ${SIDEBAR_DIVIDER}`,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <Box component="img" src="/logo192.png" alt="Swarna Group" sx={{ height: 28, width: 28, objectFit: 'contain', borderRadius: 1 }} />
              <Box>
                <Typography sx={{ fontFamily: FONT_HEADING, fontWeight: 700, fontSize: 15, color: DARK, lineHeight: 1.1 }}>
                  Swarna Group
                </Typography>
                <Typography sx={{ fontFamily: FONT_BODY, fontSize: 9, color: MUTED, letterSpacing: '0.1em', textTransform: 'uppercase', lineHeight: 1, mt: 0.25 }}>
                  {appMode === 'office' ? 'Office' : 'CRM'}
                </Typography>
              </Box>
            </Box>
            <IconButton size="small" onClick={() => setMobileOpen(false)} aria-label="Close menu" sx={{ color: SIDEBAR_TEXT, '&:hover': { color: '#fff' } }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </>
      )}

      {/* Navigation items */}
      <List sx={{ py: 1, flex: 1, px: 0.75 }}>
        {visibleNavItems.map((item) => {
          const isSelected = location.pathname === item.path;
          const iconBadgeCount = item.pageKey === 'today' ? todayActionCount + pendingBrokerApprovalCount
            : item.pageKey === 'frontDesk' ? frontDeskWaitingCount
              : item.pageKey === 'jobApplications' ? newApplicationCount
                : item.pageKey === 'brokers' ? pendingBrokerProfileCount + pendingBrokerApprovalCount : 0;
          return (
            <ListItem key={item.text} disablePadding sx={{ display: 'block', mb: 0.25 }}>
              <Tooltip title={navExpanded ? '' : item.text} placement="right" disableHoverListener={navExpanded}>
                <ListItemButton
                  component={RouterLink}
                  to={item.path}
                  onClick={() => { setMobileOpen(false); setDesktopOpen(false); }}
                  selected={isSelected}
                  sx={{
                    minHeight: 44,
                    px: navExpanded ? 1.5 : 1,
                    justifyContent: navExpanded ? 'flex-start' : 'center',
                    borderRadius: 1.5,
                    borderLeft: isSelected ? `3px solid ${GOLD}` : '3px solid transparent',
                    color: isSelected ? GOLD : SIDEBAR_TEXT,
                    bgcolor: isSelected ? SIDEBAR_SEL_BG : 'transparent',
                    '&.Mui-selected': {
                      bgcolor: SIDEBAR_SEL_BG,
                      color: GOLD,
                      '&:hover': { bgcolor: 'rgba(157,111,50,0.22)' },
                    },
                    '&:hover': {
                      bgcolor: isSelected ? SIDEBAR_SEL_BG : SIDEBAR_HOVER,
                      color: isSelected ? GOLD : DARK,
                    },
                    transition: 'all 0.15s ease',
                  }}
                >
                  <ListItemIcon sx={{
                    minWidth: navExpanded ? 36 : 0,
                    width: navExpanded ? 36 : 24,
                    mr: navExpanded ? 1.25 : 0,
                    color: 'inherit',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {iconBadgeCount > 0 ? (
                      <Badge badgeContent={iconBadgeCount} max={99} sx={{ '& .MuiBadge-badge': { fontWeight: 800, fontSize: 10, bgcolor: GOLD, color: '#fff' } }}>
                        {item.icon}
                      </Badge>
                    ) : ((item.text === 'Messages' && unreadMessageCount > 0) || (item.text === 'Meta Leads' && pendingMetaLeadCount > 0)) && !navExpanded ? (
                      <Badge badgeContent={item.text === 'Messages' ? unreadMessageCount : pendingMetaLeadCount} max={99} sx={{ '& .MuiBadge-badge': { fontWeight: 800, fontSize: 10, bgcolor: '#9d6f32', color: '#fff' } }}>
                        {item.icon}
                      </Badge>
                    ) : item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      ((item.text === 'Messages' && unreadMessageCount > 0) || (item.text === 'Meta Leads' && pendingMetaLeadCount > 0)) && navExpanded ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <span>{item.text}</span>
                          <Box sx={{
                            bgcolor: '#9d6f32', color: '#fff', borderRadius: '10px',
                            px: 0.8, py: 0.1, fontSize: 11, fontWeight: 800, lineHeight: 1.4,
                            minWidth: 20, textAlign: 'center',
                          }}>
                            {(item.text === 'Messages' ? unreadMessageCount : pendingMetaLeadCount) > 99 ? '99+' : item.text === 'Messages' ? unreadMessageCount : pendingMetaLeadCount}
                          </Box>
                        </Box>
                      ) : item.text
                    }
                    sx={{
                      display: navExpanded ? 'block' : 'none',
                      m: 0,
                      '& .MuiTypography-root': {
                        fontFamily: FONT_BODY,
                        fontWeight: isSelected ? 700 : 500,
                        fontSize: 13.5,
                        color: 'inherit',
                      },
                    }}
                  />
                </ListItemButton>
              </Tooltip>
            </ListItem>
          );
        })}
      </List>

      {/* Mobile-only: mode switch + project picker */}
      {isSmDown && (
        <>
          <Divider sx={{ borderColor: SIDEBAR_DIVIDER }} />
          {isAdmin && (
            <>
              <Box sx={{ px: 2, py: 1.5 }}>
                <Typography sx={{ fontFamily: FONT_BODY, fontSize: 10, fontWeight: 700, color: SIDEBAR_TEXT, letterSpacing: '0.08em', textTransform: 'uppercase', mb: 1 }}>
                  Switch App
                </Typography>
                {/* Gold-active pill toggle for mobile sidebar */}
                <Box sx={{ display: 'flex', bgcolor: '#f0f0f0', borderRadius: 999, border: `1px solid ${BORDER}`, p: 0.4, gap: 0 }}>
                  {(['crm', 'office'] as AppMode[]).map((mode) => (
                    <Box
                      key={mode}
                      onClick={() => {
                        window.localStorage.setItem(APP_MODE_PICKER_SKIP_ONCE_KEY, '1');
                        window.localStorage.setItem(APP_MODE_SWITCH_BYPASS_KEY, '1');
                        localStorage.setItem(APP_MODE_STORAGE_KEY, mode);
                        if (mode === 'office') localStorage.setItem(OFFICE_PROJECT_PICKER_ON_ENTER_KEY, '1');
                        navigate(mode === 'office' ? '/office' : '/', { replace: true });
                        setMobileOpen(false);
                      }}
                      sx={{
                        flex: 1,
                        textAlign: 'center',
                        py: 0.75,
                        borderRadius: 999,
                        cursor: 'pointer',
                        fontFamily: FONT_BODY,
                        fontWeight: 700,
                        fontSize: 12,
                        textTransform: 'uppercase',
                        letterSpacing: '0.06em',
                        bgcolor: appMode === mode ? GOLD : 'transparent',
                        color: appMode === mode ? '#fff' : MUTED,
                        transition: 'all 0.15s ease',
                        '&:hover': appMode !== mode ? { color: DARK } : {},
                      }}
                    >
                      {mode.toUpperCase()}
                    </Box>
                  ))}
                </Box>
              </Box>
              <Divider sx={{ borderColor: SIDEBAR_DIVIDER }} />
            </>
          )}
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: 10, fontWeight: 700, color: SIDEBAR_TEXT, letterSpacing: '0.08em', textTransform: 'uppercase', mb: 1 }}>
              Project
            </Typography>
            <FormControl size="small" fullWidth>
              <Select
                value={projectSelectValue}
                onChange={(e) => setCurrentProjectId(e.target.value as string)}
                displayEmpty
                disabled={visibleProjects.length <= 1}
                sx={{
                  color: DARK,
                  fontFamily: FONT_BODY,
                  fontSize: 13,
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: BORDER },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#bbb' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: GOLD },
                  '& .MuiSvgIcon-root': { color: MUTED },
                  '& .MuiSelect-select': { py: 1 },
                }}
                renderValue={(val) => {
                  const id = val as string;
                  if (!id) return 'Select Project';
                  const p = visibleProjects.find((x) => x.id === id);
                  return p?.name || 'Select Project';
                }}
              >
                {visibleProjects.length === 0 ? (
                  <MenuItem disabled value="">No projects</MenuItem>
                ) : (
                  visibleProjects.slice().sort((a: any, b: any) => String(a?.name || '').localeCompare(String(b?.name || ''))).map((p: any) => (
                    <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Box>
        </>
      )}

      {/* Bottom: Settings + Logout */}
      <Box sx={{ mt: 'auto' }}>
        <Divider sx={{ borderColor: SIDEBAR_DIVIDER }} />
        <List sx={{ py: 0.75, px: 0.75 }}>
          {canUseViewAs && (
            <ListItem disablePadding sx={{ display: 'block', mb: 0.25 }}>
              <Tooltip title={navExpanded ? '' : 'View as'} placement="right" disableHoverListener={navExpanded}>
                <ListItemButton
                  data-view-as-allow="true"
                  onClick={(event) => setViewAsAnchorEl(event.currentTarget)}
                  selected={Boolean(viewingAs)}
                  sx={{
                    minHeight: 44, px: navExpanded ? 1.5 : 1,
                    justifyContent: navExpanded ? 'flex-start' : 'center',
                    borderRadius: 1.5, color: viewingAs ? '#7b4f15' : SIDEBAR_TEXT,
                    borderLeft: `3px solid ${viewingAs ? GOLD : 'transparent'}`,
                    '&.Mui-selected': { bgcolor: '#fff3d6' },
                    '&.Mui-selected:hover': { bgcolor: '#f8e8c4' },
                    '&:hover': { bgcolor: SIDEBAR_HOVER, color: DARK },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: navExpanded ? 36 : 0, width: navExpanded ? 36 : 24, mr: navExpanded ? 1.25 : 0, color: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <VisibilityOutlinedIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="View as"
                    secondary={viewingAs?.name}
                    sx={{
                      display: navExpanded ? 'block' : 'none', m: 0,
                      '& .MuiListItemText-primary': { fontFamily: FONT_BODY, fontWeight: viewingAs ? 700 : 500, fontSize: 13.5, color: 'inherit' },
                      '& .MuiListItemText-secondary': { fontFamily: FONT_BODY, fontSize: 11.5, color: '#7b4f15', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
                    }}
                  />
                </ListItemButton>
              </Tooltip>
            </ListItem>
          )}
          {canPage('settings') && (
            <ListItem disablePadding sx={{ display: 'block', mb: 0.25 }}>
              <Tooltip title={navExpanded ? '' : 'Settings'} placement="right" disableHoverListener={navExpanded}>
                <ListItemButton
                  component={RouterLink}
                  to="/settings"
                  onClick={() => { setMobileOpen(false); setDesktopOpen(false); }}
                  sx={{
                    minHeight: 44, px: navExpanded ? 1.5 : 1,
                    justifyContent: navExpanded ? 'flex-start' : 'center',
                    borderRadius: 1.5, color: SIDEBAR_TEXT,
                    borderLeft: '3px solid transparent',
                    '&:hover': { bgcolor: SIDEBAR_HOVER, color: DARK },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: navExpanded ? 36 : 0, width: navExpanded ? 36 : 24, mr: navExpanded ? 1.25 : 0, color: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <SettingsOutlinedIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Settings" sx={{ display: navExpanded ? 'block' : 'none', m: 0, '& .MuiTypography-root': { fontFamily: FONT_BODY, fontWeight: 500, fontSize: 13.5, color: 'inherit' } }} />
                </ListItemButton>
              </Tooltip>
            </ListItem>
          )}
          <ListItem disablePadding sx={{ display: 'block' }}>
            <Tooltip title={navExpanded ? '' : 'Logout'} placement="right" disableHoverListener={navExpanded}>
              <ListItemButton
                onClick={handleLogout}
                sx={{
                  minHeight: 44, px: navExpanded ? 1.5 : 1,
                  justifyContent: navExpanded ? 'flex-start' : 'center',
                  borderRadius: 1.5, color: SIDEBAR_TEXT,
                  borderLeft: '3px solid transparent',
                  '&:hover': { bgcolor: 'rgba(220,60,60,0.12)', color: '#ff6b6b' },
                }}
              >
                <ListItemIcon sx={{ minWidth: navExpanded ? 36 : 0, width: navExpanded ? 36 : 24, mr: navExpanded ? 1.25 : 0, color: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <LogoutOutlinedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Logout" sx={{ display: navExpanded ? 'block' : 'none', m: 0, '& .MuiTypography-root': { fontFamily: FONT_BODY, fontWeight: 500, fontSize: 13.5, color: 'inherit' } }} />
              </ListItemButton>
            </Tooltip>
          </ListItem>
        </List>
      </Box>
    </Box>
  );
  // ──────────────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <CssBaseline />

      {/* ── Top AppBar ─────────────────────────────────────────────────────── */}
      <AppBar
        component="nav"
        position="fixed"
        data-role="app-header"
        elevation={0}
        sx={{
          zIndex: (t) => t.zIndex.drawer + 1,
          bgcolor: '#fff',
          color: DARK,
          borderBottom: `2px solid ${GOLD}`,
          boxShadow: '0 1px 8px rgba(0,0,0,0.06)',
        }}
      >
        <Toolbar sx={{ gap: 1, minHeight: `${APPBAR_H}px !important` }}>

          {/* Hamburger */}
          <IconButton
            aria-label="toggle drawer"
            edge="start"
            onClick={isSmDown ? handleDrawerToggle : handleDesktopToggle}
            sx={{ color: DARK, mr: 0.5 }}
          >
            <MenuIcon />
          </IconButton>

          {/* Brand logo + label */}
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0, overflow: 'hidden' }}>
            <Box
              component="img"
              src="/logo192.png"
              alt="Swarna Group"
              sx={{ height: { xs: 28, sm: 32 }, width: 'auto', flexShrink: 0, display: 'block' }}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', minWidth: 0 }}>
              <Typography sx={{ fontFamily: FONT_HEADING, fontWeight: 700, fontSize: { xs: 14, sm: 16 }, color: DARK, lineHeight: 1.1, whiteSpace: 'nowrap', userSelect: 'none' }}>
                Swarna Group
              </Typography>
              <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 600, fontSize: { xs: 9, sm: 10 }, color: MUTED, letterSpacing: '0.1em', textTransform: 'uppercase', lineHeight: 1, userSelect: 'none' }}>
                {appMode === 'office' ? 'Office' : 'CRM'}
              </Typography>
            </Box>
          </Box>

          {/* Mobile: report a problem */}
          <Box sx={{ display: { xs: 'flex', sm: 'none' }, alignItems: 'center', mr: 0.5 }}>
            <IssueReporter compact projectName={visibleProjects.find((x: any) => x.id === projectSelectValue)?.name || null} />
          </Box>
          {/* Mobile: bell only */}
          {appMode === 'crm' && !viewingAs && (
            <Box sx={{ display: { xs: 'flex', sm: 'none' }, alignItems: 'center' }}>
              <Tooltip title="Reminders">
                <IconButton onClick={(e) => setReminderAnchorEl(e.currentTarget)} aria-label="Open reminders" sx={{ color: DARK }}>
                  <Badge badgeContent={reminders.length} color="error" max={99}>
                    <NotificationsNoneOutlinedIcon />
                  </Badge>
                </IconButton>
              </Tooltip>
            </Box>
          )}

          {/* Desktop: toggle + project + bell */}
          <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 1.5 }}>

            {/* CRM / Office pill toggle — gold active */}
            {isAdmin && (
              <Box sx={{
                display: 'flex', alignItems: 'center',
                bgcolor: '#f4f4f4',
                border: `1px solid ${BORDER}`,
                borderRadius: 999,
                p: 0.4,
                gap: 0,
              }}>
                {(['crm', 'office'] as AppMode[]).map((mode) => (
                  <Box
                    key={mode}
                    onClick={() => {
                      window.localStorage.setItem(APP_MODE_PICKER_SKIP_ONCE_KEY, '1');
                      window.localStorage.setItem(APP_MODE_SWITCH_BYPASS_KEY, '1');
                      localStorage.setItem(APP_MODE_STORAGE_KEY, mode);
                      if (mode === 'office') localStorage.setItem(OFFICE_PROJECT_PICKER_ON_ENTER_KEY, '1');
                      navigate(mode === 'office' ? '/office' : '/', { replace: true });
                    }}
                    sx={{
                      px: 2,
                      py: 0.625,
                      borderRadius: 999,
                      cursor: 'pointer',
                      fontFamily: FONT_BODY,
                      fontWeight: 700,
                      fontSize: 12,
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                      bgcolor: appMode === mode ? GOLD : 'transparent',
                      color: appMode === mode ? '#fff' : MUTED,
                      transition: 'all 0.15s ease',
                      userSelect: 'none',
                      '&:hover': appMode !== mode ? { color: DARK } : {},
                    }}
                  >
                    {mode.toUpperCase()}
                  </Box>
                ))}
              </Box>
            )}

            {/* Report a problem: on every CRM page. */}
            <IssueReporter projectName={visibleProjects.find((x: any) => x.id === projectSelectValue)?.name || null} />
            {/* Project selector */}
            <FormControl size="small" sx={{ minWidth: 160, maxWidth: 240 }}>
              <Select
                value={projectSelectValue}
                onChange={(e) => setCurrentProjectId(e.target.value as string)}
                displayEmpty
                disabled={visibleProjects.length <= 1}
                sx={{
                  color: DARK,
                  fontFamily: FONT_BODY,
                  fontSize: 13,
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: BORDER },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#bbb' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: GOLD },
                  '& .MuiSvgIcon-root': { color: MUTED },
                  '& .MuiSelect-select': { py: 0.875 },
                }}
                renderValue={(val) => {
                  const id = val as string;
                  if (!id) return 'Select Project';
                  const p = visibleProjects.find((x) => x.id === id);
                  return p?.name || 'Select Project';
                }}
              >
                {visibleProjects.length === 0 ? (
                  <MenuItem disabled value="">No projects</MenuItem>
                ) : (
                  visibleProjects.slice().sort((a: any, b: any) => String(a?.name || '').localeCompare(String(b?.name || ''))).map((p: any) => (
                    <MenuItem key={p.id} value={p.id} sx={{ fontFamily: FONT_BODY, fontSize: 13 }}>{p.name}</MenuItem>
                  ))
                )}
              </Select>
            </FormControl>

            {/* Bell */}
            {appMode === 'crm' && !viewingAs && (
              <Tooltip title="Reminders">
                <IconButton onClick={(e) => setReminderAnchorEl(e.currentTarget)} aria-label="Open reminders" sx={{ color: DARK }}>
                  <Badge badgeContent={reminders.length} color="error" max={99}>
                    <NotificationsNoneOutlinedIcon />
                  </Badge>
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Toolbar>
        {viewingAs && (
          <Box
            data-view-as-allow="true"
            sx={{
              height: 42,
              px: { xs: 1.5, sm: 3 },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.5,
              bgcolor: '#fff3d6',
              borderTop: '1px solid #e6c98f',
              color: '#6f4510',
            }}
          >
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: 13, fontWeight: 700, textAlign: 'center' }}>
              Viewing as {viewingAs.name} · Read only
            </Typography>
            <Button
              data-view-as-allow="true"
              size="small"
              variant="outlined"
              onClick={() => { stopViewAs(); navigate('/', { replace: true }); }}
              sx={{ minWidth: 0, px: 1.25, py: 0.25, borderColor: GOLD, color: '#6f4510', fontWeight: 700, textTransform: 'none' }}
            >
              Exit view
            </Button>
          </Box>
        )}
      </AppBar>
      {/* ─────────────────────────────────────────────────────────────────── */}

      <Menu
        anchorEl={viewAsAnchorEl}
        open={Boolean(viewAsAnchorEl)}
        onClose={() => setViewAsAnchorEl(null)}
        slotProps={{ paper: { sx: { minWidth: 230, maxHeight: 360 } } }}
      >
        {viewingAs && (
          <MenuItem data-view-as-allow="true" onClick={() => { void selectViewAsUser(''); }} sx={{ fontFamily: FONT_BODY, fontSize: 13, color: '#9b241c', fontWeight: 700 }}>
            Exit view
          </MenuItem>
        )}
        {visibleViewAsUsers.length === 0 && <MenuItem disabled>No team members</MenuItem>}
        {visibleViewAsUsers.map((user) => (
          <MenuItem
            key={user.uid}
            data-view-as-allow="true"
            selected={viewingAs?.uid === user.uid}
            onClick={() => { void selectViewAsUser(user.uid); }}
            sx={{ fontFamily: FONT_BODY, fontSize: 13 }}
          >
            {user.name} · {user.roleId.replace(/-/g, ' ')}
          </MenuItem>
        ))}
      </Menu>

      {/* Reminders popover */}
      <Popover
        open={remindersOpen}
        anchorEl={reminderAnchorEl}
        onClose={() => setReminderAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { width: 360, maxWidth: '95vw', mt: 0.5, borderRadius: 2 } }}
      >
        <List dense sx={{ p: 0 }}>
          {reminders.length === 0 && (
            <ListItem>
              <ListItemText primary="No active reminders" secondary="Upcoming/Due/Overdue items will appear here." />
            </ListItem>
          )}
          {reminders.map((r, idx) => {
            const isRead = readReminderKeys.has(r.reminderKey);
            const statusLabel = r.status === 'due' ? 'Due now' : r.status === 'upcoming' ? 'Upcoming' : 'Overdue';
            return (
              <React.Fragment key={r.reminderKey}>
                <ListItemButton onClick={async () => { await markReminderRead(r); navigate(`/follow-ups?leadId=${r.id}`); setReminderAnchorEl(null); }}>
                  <ListItemText
                    primary={(
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        {!isRead && <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: 'primary.main', flexShrink: 0 }} />}
                        <Typography variant="body2" sx={{ fontWeight: isRead ? 500 : 800 }}>{r.name}</Typography>
                      </Box>
                    )}
                    secondary={`${r.kind === 'visit' ? 'Visit' : 'Follow-up'} • ${statusLabel} • ${r.dueAt.toLocaleDateString()} ${r.dueAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })}`}
                  />
                </ListItemButton>
                {idx < reminders.length - 1 && <Divider component="li" />}
              </React.Fragment>
            );
          })}
        </List>
      </Popover>

      {/* Mode picker fullscreen dialog — keep existing blue gradient */}
      <Dialog open={modePickerOpen} fullScreen PaperProps={{ sx: { background: 'linear-gradient(135deg, #0a2a6a 0%, #0e3b8f 45%, #1a4ea3 100%)', color: '#fff' } }}>
        <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', px: 2, py: 3 }}>
          <Box sx={{ width: '100%', maxWidth: 920 }}>
            <Typography variant="h5" sx={{ fontWeight: 900, textAlign: 'center', mb: 2 }}>Select App</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(280px, 360px))' }, justifyContent: 'center', gap: 1.5 }}>
              <Paper variant="outlined" onClick={() => { window.localStorage.setItem(APP_MODE_PICKER_SKIP_ONCE_KEY, '1'); localStorage.setItem(APP_MODE_STORAGE_KEY, 'crm'); setModePickerOpen(false); navigate('/', { replace: true }); }} sx={{ p: 2, cursor: 'pointer', borderColor: appMode === 'crm' ? '#90caf9' : 'rgba(255,255,255,0.4)', bgcolor: appMode === 'crm' ? 'rgba(21,101,192,0.18)' : 'rgba(255,255,255,0.12)', color: '#fff', textAlign: 'center' }}>
                <Typography sx={{ fontWeight: 800, fontSize: '1.1rem' }}>CRM</Typography>
              </Paper>
              <Paper variant="outlined" onClick={() => { window.localStorage.setItem(APP_MODE_PICKER_SKIP_ONCE_KEY, '1'); localStorage.setItem(APP_MODE_STORAGE_KEY, 'office'); localStorage.setItem(OFFICE_PROJECT_PICKER_ON_ENTER_KEY, '1'); navigate('/office', { replace: true }); }} sx={{ p: 2, cursor: 'pointer', borderColor: appMode === 'office' ? '#90caf9' : 'rgba(255,255,255,0.4)', bgcolor: appMode === 'office' ? 'rgba(21,101,192,0.18)' : 'rgba(255,255,255,0.12)', color: '#fff', textAlign: 'center' }}>
                <Typography sx={{ fontWeight: 800, fontSize: '1.1rem' }}>Office</Typography>
              </Paper>
            </Box>
          </Box>
        </Box>
      </Dialog>

      {/* ── Main layout (below fixed AppBar) ─────────────────────────────── */}
      <Box sx={{ display: 'flex', flex: 1, minHeight: 0, mt: `${headerHeight}px` }}>

        {/* Sidebar nav */}
        <Box
          component="nav"
          sx={{
            position: { xs: 'static', sm: 'sticky' },
            top: { xs: 'auto', sm: `${headerHeight}px` },
            alignSelf: 'flex-start',
            height: { sm: `calc(100vh - ${headerHeight}px)` },
            flexShrink: 0,
            borderRight: { xs: 'none', sm: `1px solid ${BORDER}` },
          }}
        >
          {/* Mobile temporary drawer */}
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{ keepMounted: true }}
            sx={{
              display: { xs: 'block', sm: 'none' },
              '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: drawerWidth,
                bgcolor: SIDEBAR_BG,
              },
            }}
          >
            {drawer}
          </Drawer>

          {/* Desktop permanent drawer */}
          <Drawer
            variant="permanent"
            open={desktopOpen}
            sx={{
              display: { xs: 'none', sm: 'block' },
              width: desktopOpen ? drawerWidth : collapsedWidth,
              flexShrink: 0,
              height: '100%',
              '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: desktopOpen ? drawerWidth : collapsedWidth,
                transition: 'width 0.2s ease',
                overflowX: 'hidden',
                position: 'relative',
                height: '100%',
                borderRight: 'none',
                bgcolor: SIDEBAR_BG,
              },
            }}
          >
            {drawer}
          </Drawer>
        </Box>

        {/* Page content */}
        <Box
          component="main"
          onClickCapture={blockPreviewInteraction}
          onSubmitCapture={blockPreviewInteraction}
          onKeyDownCapture={blockPreviewInteraction}
          sx={{
            p: {
              xs: isFrontDeskPage ? 0 : '5px',
              sm: isFrontDeskPage || location.pathname.startsWith('/leads') || location.pathname.startsWith('/follow-ups') || (location.pathname.startsWith('/messages') || location.pathname.startsWith('/whatsapp')) ? 0 : 3,
            },
            width: '100%',
            maxWidth: 'none',
            mx: 0,
            minWidth: 0,
            flex: 1,
            overflow: (location.pathname.startsWith('/messages') || location.pathname.startsWith('/whatsapp')) ? 'hidden' : 'auto',
            height: (location.pathname.startsWith('/messages') || location.pathname.startsWith('/whatsapp')) ? `calc(100vh - ${headerHeight}px)` : 'auto',
          }}
        >
          <Paper elevation={0} sx={{ p: isFrontDeskPage ? 0 : 0.125, boxShadow: 'none', bgcolor: isFrontDeskPage ? '#f7f5f1' : undefined, minHeight: isFrontDeskPage ? `calc(100vh - ${headerHeight}px)` : undefined, height: (location.pathname.startsWith('/messages') || location.pathname.startsWith('/whatsapp')) ? '100%' : 'auto' }}>
            <Outlet />
          </Paper>
        </Box>
      </Box>

      {/* Keep My Today visible in View as so the preview matches the selected user. */}
      {appMode === 'crm' && <WhatsAppNotificationPanel readOnly={Boolean(viewingAs)} />}
    </Box>
  );
}
